<template>
  <div>
    <card-wrapper :title="$t('requests.title')" id="requests">
      <v-form v-model="validForm" class="m-t-10 m-b-20 d-flex flex-column requests-spacing">
        <m-form-row>
          <template v-slot:custom-label>
            <div class="full-width d-flex justify-end pr-8">{{ $t('requests.email') }}</div>
          </template>

          <template v-slot:default>
            <m-text-field
              data-cy="email"
              v-model="formEmail"
              :placeholder="$t('requests.email-placeholder')"
              readonly></m-text-field>
          </template>
        </m-form-row>

        <m-form-row>
          <template v-slot:custom-label>
            <div class="full-width d-flex justify-end pr-8">{{ $t('requests.subject') }}*</div>
          </template>

          <template v-slot:default>
            <m-text-field
              data-cy="asunto"
              :rules="[isRequired, isEmpty, r => isMinText(r, 3)]"
              v-model="formSubject"
              :placeholder="$t('requests.subject')"
              autofocus></m-text-field>
          </template>
        </m-form-row>

        <m-form-row>
          <template v-slot:custom-label>
            <div class="full-width d-flex justify-end align-self-start pr-8">{{ $t('requests.message') }}*</div>
          </template>

          <template v-slot:default>
            <m-textarea
              id="message"
              :rules="[isRequired, isEmpty, r => isMinText(r, 3)]"
              v-model="formMessage"
              solo
              auto-grow />
          </template>
        </m-form-row>

        <m-form-row>
          <template v-slot:custom-label>
            <div class="full-width d-flex justify-end align-self-start pr-8">
              {{ $t('requests.attachment') }}
            </div>
          </template>

          <template v-slot:default>
            <v-row no-gutters class="align-center">
              <m-text-field data-cy="fileName" v-model="fileName" readonly class="attachment-label"></m-text-field>

              <custom-input-file
                :key="`ticket_attachments_${refreshKey}`"
                @importAction="handleUploadAttachment"
                accept="image/*,application/pdf,text/plain"
                class="attachment-button"
                type="s3-attachment" />
            </v-row>
          </template>
        </m-form-row>

        <div class="col-7 text-right requests-spacing">
          <v-btn
            id="submit_request"
            class="btn-primary"
            color="primary"
            :loading="loading"
            :disabled="loading || !validForm"
            @click="handleCreateTicket">
            {{ $t('buttons.send') }}
          </v-btn>
        </div>
      </v-form>

      <mapit-dialog :dialog="isDialogOpen" @close="handleCloseDialog">
        <template v-slot:header-icon>
          <v-icon v-if="serverError" color="warning"> fa fa-octagon-xmark</v-icon>
          <v-icon v-else color="secondary">fa-circle-check</v-icon>
        </template>

        <template v-slot:header-text>
          <div class="text-center">
            <p v-if="serverError">{{ $t('requests.error-title') }}</p>
            <p v-else>{{ $t('requests.success-title') }}</p>
          </div>
        </template>

        <template v-slot:body>
          <div class="text-center">
            <p v-if="serverError">{{ $t('requests.error-body') }}</p>
            <p v-else>{{ $t('requests.success-body') }}</p>
          </div>
        </template>

        <template v-slot:footer-center>
          <v-btn class="primary" @click="handleCloseDialog">{{ $t('buttons.finish') }}</v-btn>
        </template>
      </mapit-dialog>
    </card-wrapper>
  </div>
</template>

<script lang="ts">
import {computed, defineComponent, onMounted, ref} from '@vue/composition-api'

import MapitDialog from '@/components/dialog/MapitDialog.vue'
import CardWrapper from '@/components/general/CardWrapper.vue'
import CustomInputFile from '@/components/import/CustomInputFile.vue'
import {useContact} from '@/composables/contact'
import {isEmpty, isMaxText, isMinText, isRequired} from '@/helpers/validations'
import router from '@/router'

export default defineComponent({
  methods: {isMaxText, isMinText},
  components: {
    CustomInputFile,
    MapitDialog,
    CardWrapper,
  },
  setup(props, {root}) {
    const refreshKey = ref(0)

    const formEmail = ref('')
    const formSubject = ref('')
    const formMessage = ref('')
    const formAttachment = ref('')
    const fileName = ref<string | null>(null)
    const validForm = ref(false)

    const isDialogOpen = ref(false)
    const serverError = ref(false)

    const {loading, sendTicket, addAttachment} = useContact()

    const user = computed(() => {
      return root.$store.getters.getUser
    })

    onMounted(() => {
      formEmail.value = user.value.login
    })

    async function handleUploadAttachment(data: any) {
      try {
        const {key, name} = addAttachment(data)
        formAttachment.value = key
        fileName.value = name
      } catch (e) {
        console.log(e)
      }

      refreshKey.value++
    }

    async function handleCreateTicket() {
      serverError.value = false

      try {
        await sendTicket({
          email: formEmail.value,
          subject: formSubject.value,
          record: formMessage.value,
          attachment: formAttachment.value,
        })
      } catch (e) {
        console.error(e)
        serverError.value = true
      }

      isDialogOpen.value = true
    }

    function handleCloseDialog() {
      isDialogOpen.value = false

      if (!serverError.value) {
        router.push({name: 'Dashboard'})
      }
    }

    return {
      fileName,
      formAttachment,
      formEmail,
      formMessage,
      formSubject,
      handleCloseDialog,
      handleCreateTicket,
      handleUploadAttachment,
      isDialogOpen,
      isEmpty,
      isRequired,
      loading,
      refreshKey,
      serverError,
      validForm,
    }
  },
})
</script>

<style scoped>
.requests-spacing {
  max-width: 900px;
  margin: 0 auto;
}

.attachment-label {
  width: 65%;
  padding-right: 15px;
}

.attachment-button {
  width: 35% !important;
}
</style>
